
import { defineComponent, ref, computed, onMounted, Ref } from 'vue';
import { useGettext } from '@jshmrtn/vue3-gettext';
import { useRoute } from 'vue-router';
import useNavigateTo from '@/composables/useNavigateTo';
import useHandleKey from '@/composables/useHandleKey';
import { Password } from '@/interfaces/password.interface';
import { CxServiceUnavailableError } from '@/classes/error.class';
import CxResetForm from '@/app/CxResetForm.vue';
import CxSpinner from '@/components/CxSpinner.vue';
import resetApi from '@/http/reset.api';
import { ErrorType, useDisplayError } from '@/composables/useDisplayError';

export default defineComponent({
  name: 'CxReset',
  components: { CxResetForm, CxSpinner },
  setup() {
    // Use composables and plugins
    const { navigateToLogin, navigateToRecovery } = useNavigateTo();
    const { handleKeyDown } = useHandleKey();
    const language = useGettext();
    const route = useRoute();
    const { currentErrorType, errorText } = useDisplayError(language);

    // Data
    const views = ref({
      resetForm: 'resetForm',
      tokenError: 'tokenError',
      resetSuccess: 'resetSuccess'
    });
    const view: Ref<string> = ref(views.value.resetForm);
    const resetError: Ref<ErrorType | null> = ref(null);
    const username: Ref<string | null> = ref(null);
    const token: Ref<string | string[] | null> = ref(null);
    const uidb64: Ref<string | string[] | null> = ref(null);
    const resetSuccess: Ref<boolean> = ref(false);
    const isTokenValidationInProgress: Ref<boolean> = ref(true);
    const isResetQueryInProgress: Ref<boolean> = ref(false);

    // Computed
    const currentView = computed((): string => {
      if (!token.value || !username.value || currentErrorType.value) return views.value.tokenError;
      if (!resetSuccess.value) return views.value.resetForm;
      return views.value.resetSuccess;
    });
    const translations = computed((): {
      recovery: string;
      request: string;
      headline: string;
      note: string;
      serviceUnavailable: string;
      unexpectedError: string;
      success: string;
      backToLogin: string;
      passwordsDoNotMatch: string;
      } => {
      return {
        recovery: language.$gettext('Password Recovery'),
        request: language.$gettext('Request a new password reset link'),
        headline: language.$gettext('Dear Customers'),
        note: language.$gettext('Please note that if you are retrieving your updated username and password reset link, you will need to use the email address attached to your account'),
        serviceUnavailable: language.$gettext('Sorry, service is unavailable - please try again later'),
        unexpectedError: language.$gettext('Unexpected error has occurred - please try again later or contact support team'),
        success: language.$gettext('Your password has successfully been reset. You can use it right now on the login page'),
        backToLogin: language.$gettext('Back to login'),
        passwordsDoNotMatch: language.$gettext('Passwords didn\'t match')
      };
    });

    // Methods
    const setNewPassword = async (newPassword: Password): Promise<void> => {
      try {
        isResetQueryInProgress.value = true;
        resetSuccess.value = await resetApi.resetPassword(newPassword, token.value, uidb64.value);
        if (!resetSuccess.value) resetError.value = ErrorType.PasswordsDoNotMatch;
      } catch (error) {
        if (error instanceof CxServiceUnavailableError) resetError.value = ErrorType.ServiceUnavailable;
        else resetError.value = ErrorType.UnexpectedError;
      } finally {
        isResetQueryInProgress.value = false;
      }
    };
    const validateResetToken = async (): Promise<void> => {
      const resetToken = route.params.token;
      const resetUidb64 = route.params.uidb64;
      if (!resetToken || !resetUidb64) currentErrorType.value = ErrorType.InvalidPasswordResetLink;
      token.value = resetToken;
      uidb64.value = resetUidb64;
      try {
        username.value = await resetApi.validateResetToken(token.value, uidb64.value);
        if (!username.value) currentErrorType.value = ErrorType.InvalidPasswordResetLink;
      } catch (error) {
        if (error instanceof CxServiceUnavailableError) currentErrorType.value = ErrorType.ServiceUnavailable;
        else currentErrorType.value = ErrorType.UnexpectedError;
      } finally {
        isTokenValidationInProgress.value = false;
      }
    };

    // Lifecycle Hooks
    onMounted(validateResetToken);

    // Expose properties and methods
    return {
      // Data
      view,
      views,
      username,
      resetError,
      isResetQueryInProgress,
      isTokenValidationInProgress,
      currentErrorType,
      // Computed
      currentView,
      translations,
      errorText,
      // Methods
      setNewPassword,
      navigateToLogin,
      navigateToRecovery,
      handleKeyDown
    };
  }
});
